<script setup lang="ts">
import type { Step } from "@/types"
import type { UseStepperReturn } from "@vueuse/core"
import HeaderStep from "@/components/FileImport/Dialog/HeaderStep.vue"
import { Decorator, PhaseSteps } from "@/types"
import { computed, toRef } from "vue"

interface Props {
  stepper: UseStepperReturn<any, any[], any>
  tooltipText?: string
  disableImport: boolean
}

interface Emits {
  (event: "next", step: PhaseSteps): void
  (event: "step", step: PhaseSteps): void
  (event: "import"): void
}
const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const stepper = toRef(props, "stepper")

const showNextButton = computed(() => {
  return !stepper.value.isLast.value && stepper.value.current.value.showNext()
})

const showImportButton = computed(() => {
  return stepper.value.isLast.value
})

const importDisabled = computed(() => {
  return (
    stepper.value.steps.value.some((step) => {
      return (
        step.title !== PhaseSteps.PREVIEW &&
        step.decorator() !== Decorator.VALID
      )
    }) || props.disableImport
  )
})

function handleNext() {
  emit("next", stepper.value.next.value.title)
}

function handleImport() {
  emit("import")
}

function handleStepClick(step: Step) {
  emit("step", step.title)
}

function isStepDisabled(step: Step) {
  return (
    !showNextButton.value &&
    !showImportButton.value &&
    !stepper.value.isCurrent(step)
  )
}
</script>

<template>
  <div>
    <h1
      class="font-vastago text-xl font-bold"
      data-test="dialog-header-import"
    >
      Import
    </h1>
    <slot />
  </div>

  <div class="justify-center absolute top-3 grid place-content-center w-full pointer-events-none">
    <div class="flex items-center gap-6 rounded-lg bg-white px-4 py-1 drop-shadow-md pointer-events-auto">
      <div class="flex items-center gap-6">
        <button
          v-for="(step, stepIndex) in stepper.steps.value"
          :key="step.id"
          v-tooltip.icon="isStepDisabled(step) ? tooltipText : ''"

          :class="{
            'text-gray-700': isStepDisabled(step),
          }"
          :disabled="isStepDisabled(step)"
          @click="handleStepClick(step)"
        >
          <HeaderStep
            :title="`${stepIndex + 1}.${step.title}`"
            :decorator="step.decorator()"
            :is-current="stepper.isCurrent(step)"
          />
        </button>
      </div>

      <KButton
        v-if="showNextButton"
        size="small"
        class="h-6 px-7"
        data-test="next-btn"
        @click="handleNext"
      >
        Next
      </KButton>
      <KButton
        v-if="showImportButton"
        :disabled="importDisabled"
        size="small"
        analytics-id="import-file"
        class="flex h-6 shrink-0 px-7"
        data-test="import-btn"
        @click="handleImport"
      >
        Import data
      </KButton>
    </div>
  </div>
</template>
